/** Layout-only contract with the Darkserver sheet. Never carries credentials. */
export type ChildInsets = { top: number; right: number; bottom: number; left: number }

let confirmed = false
let booted = false
let documentId = ''
let handshakeId: string | null = null
let revision = 0
const retiredHandshakes = new Set<string>()
const subscribers = new Set<() => void>()

export function isFramed(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.top !== window
  } catch {
    return true
  }
}

export function isDarkserverEmbedded(): boolean {
  return confirmed && isFramed()
}

export function subscribeEmbedding(callback: () => void): () => void {
  subscribers.add(callback)
  return () => subscribers.delete(callback)
}

/** Stable primitive snapshot for React useSyncExternalStore. */
export function embeddingSnapshot(): boolean {
  return isDarkserverEmbedded()
}

/**
 * #651 listener: the sheet uses the same URL scheme and hostname as Embertime,
 * but port 8030 rather than the dedicated Embertime port 8034. HTTP is only
 * accepted on loopback for isolated local browser tests/development.
 * Neither a query parameter nor a message may supply an origin.
 */
export function allowedParentOrigin(): string | null {
  if (typeof window === 'undefined') return null
  const { protocol, hostname, port } = window.location
  const loopback = hostname === 'localhost' || hostname === '127.0.0.1'
  if (port !== '8034' || (protocol !== 'https:' && !(loopback && protocol === 'http:'))) return null
  return `${protocol}//${hostname}:8030`
}

function validInsets(value: unknown): value is ChildInsets {
  if (!value || Array.isArray(value) || typeof value !== 'object') return false
  const insets = value as Record<string, unknown>
  if (Object.keys(insets).sort().join(',') !== 'bottom,left,right,top') return false
  const vertical = Math.max(0, window.innerHeight)
  const horizontal = Math.max(0, window.innerWidth)
  for (const side of ['top', 'right', 'bottom', 'left'] as const) {
    const number = insets[side]
    if (typeof number !== 'number' || !Number.isFinite(number) || number < 0) return false
    if (number > (side === 'top' || side === 'bottom' ? vertical : horizontal / 2)) return false
  }
  return (
    insets.top === 0 &&
    (insets.bottom as number) <= vertical &&
    (insets.left as number) + (insets.right as number) <= horizontal
  )
}

function handleLayout(event: MessageEvent): void {
  const origin = allowedParentOrigin()
  if (!isFramed() || !origin || event.source !== window.parent || event.origin !== origin) return
  const data: unknown = event.data
  if (!data || Array.isArray(data) || typeof data !== 'object') return
  const message = data as Record<string, unknown>
  if (
    message.type !== 'darkember:app-blatt:layout' ||
    message.version !== 1 ||
    message.embedded !== true ||
    !validInsets(message.insets) ||
    typeof message.handshakeId !== 'string' ||
    !/^[a-f0-9]{32}$/.test(message.handshakeId) ||
    typeof message.revision !== 'number' ||
    !Number.isSafeInteger(message.revision) ||
    message.revision < 1 ||
    retiredHandshakes.has(message.handshakeId) ||
    (message.handshakeId === handshakeId ? message.revision <= revision : message.revision !== 1)
  )
    return

  if (handshakeId && handshakeId !== message.handshakeId) {
    retiredHandshakes.add(handshakeId)
  }
  handshakeId = message.handshakeId
  revision = message.revision
  const insets = message.insets
  const root = document.documentElement
  for (const side of ['top', 'right', 'bottom', 'left'] as const) {
    root.style.setProperty(`--app-safe-${side}`, `${insets[side]}px`)
  }
  root.dataset.darkserverEmbedded = 'true'
  if (!confirmed) {
    confirmed = true
    for (const subscriber of subscribers) subscriber()
  }
  // All layout consumers use these root variables; acknowledgment follows
  // their application and the synchronous embedding-state notification.
  window.parent.postMessage(
    { type: 'darkember:app-blatt:angewendet', version: 1, documentId, handshakeId, revision },
    origin,
  )
}

function sendReady(): void {
  const origin = allowedParentOrigin()
  if (!isFramed() || !origin) return
  window.parent.postMessage({ type: 'darkember:app-blatt:bereit', version: 1, documentId }, origin)
}

/** Run before height calculation and React mount. Safe across repeated boots. */
export function bootAppEmbedding(): void {
  if (typeof window === 'undefined' || booted) return
  booted = true
  // getRandomValues also works in the HTTP loopback browser laboratory.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  documentId = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  window.addEventListener('message', handleLayout)
  // Both windows can return from BFCache with their existing contract.
  // A load/retry in #884 may instead start a new handshake for this document.
  window.addEventListener('pageshow', sendReady)
  window.addEventListener('resize', sendReady)
  window.addEventListener('orientationchange', sendReady)
  sendReady()
}
